import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, LogIn } from 'lucide-react';

export function DevLoginPage() {
  const navigate = useNavigate();
  const { setAuthToken, setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const availableEmails = [
    { email: 'lukasz.sikorra@gmail.com', role: 'Admin' },
    { email: 'lukasz@plexisystem.pl', role: 'Admin' },
    { email: 'handlowiec@plexisystem.pl', role: 'Handlowiec' },
    { email: 'produkcja@plexisystem.pl', role: 'Kierownik Produkcji' },
    { email: 'marketing@plexisystem.pl', role: 'Marketing' },
    { email: 'ksiegowosc@plexisystem.pl', role: 'Księgowość' }
  ];

  const handleLogin = async (selectedEmail?: string) => {
    const loginEmail = selectedEmail || email;
    if (!loginEmail) {
      setError('Wybierz lub wpisz email');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/auth/dev-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: loginEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Błąd logowania');
      }

      if (data.success && data.token && data.user) {
        // Zapisz token i dane użytkownika
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_data', JSON.stringify(data.user));
        
        // Ustaw w kontekście
        setAuthToken(data.token);
        setUser(data.user);
        
        // Przekieruj do dashboardu
        navigate('/dashboard');
      } else {
        throw new Error('Nieprawidłowa odpowiedź serwera');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Błąd logowania');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-orange-500 mb-2">PlexiSystem</h1>
          <p className="text-gray-400">Tryb deweloperski</p>
        </div>

        <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-white mb-2">Szybkie logowanie</h2>
              <p className="text-gray-400">Wybierz konto testowe</p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Lista kont testowych */}
            <div className="space-y-2">
              {availableEmails.map((account) => (
                <button
                  key={account.email}
                  onClick={() => handleLogin(account.email)}
                  disabled={isLoading}
                  className="w-full p-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="text-left">
                    <div className="font-medium">{account.email}</div>
                    <div className="text-sm text-gray-400">{account.role}</div>
                  </div>
                  <LogIn className="h-5 w-5 text-gray-400 group-hover:text-orange-400 transition-colors" />
                </button>
              ))}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-400">lub</span>
              </div>
            </div>

            {/* Ręczne wpisanie emaila */}
            <div className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Wpisz email autoryzowany"
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-400 focus:outline-none"
                disabled={isLoading}
              />
              <button
                onClick={() => handleLogin()}
                disabled={isLoading || !email}
                className="w-full p-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Logowanie...' : 'Zaloguj'}
              </button>
            </div>

            <div className="text-center">
              <a 
                href="/login" 
                className="text-sm text-gray-400 hover:text-orange-400 transition-colors"
              >
                Wróć do normalnego logowania
              </a>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          ⚠️ Tylko dla środowiska deweloperskiego
        </p>
      </div>
    </div>
  );
}
